import GridCard from '@Guidelines/Component/GridCard';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import ProviderCardActions from '@UI/Pages/Administration/AiEngines/Sections/ProvidersSection/ProviderCard/ProviderCardActions';
import type AiProviderView from '@UI/Store/AiConfiguration/Views/AiProviderView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Tag } from '@verclam/ui-kit';

const PROVIDER_CARD_TRACK_COUNT = 3;

const headerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const nameStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const subTitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const fieldRowStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const fieldLabelStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const fieldValueStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.ink};
`;

const badgeRowStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
`;

const testMessageStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.states.danger.ink};
`;

type Props = {
	readonly provider: AiProviderView;
};

/**
 * One declared provider, laid out as a three-track subgrid: a header block (title + subtitle, tightly
 * stacked so the subtitle sits close to its title rather than a full subgrid row away), body, actions.
 * The header controls its own internal spacing; the card's `rowGap` only separates the three tracks
 * from each other. Everything that varies between providers — base URL, badges, the last test's
 * message — stays inside the body track rather than growing the track count, so every card in the grid
 * keeps the same three rows to align on. The body carries identity, base URL, a proof the key is
 * registered, and the outcome of its last connection test — never the key itself, on any test outcome.
 * `lastTestSucceeded` carries three states (`null`, `false`, `true`): each renders its own badge, never
 * a boolean toggle.
 */
const ProviderCard: React.FC<Props> = ({ provider }) => {
	const testBadge = React.useMemo(() => {
		if (provider.lastTestSucceeded === null) {
			return <Tag severity="secondary" value="Connexion jamais testée" />;
		}

		if (provider.lastTestSucceeded) {
			return (
				<Tag
					severity="success"
					value={
						provider.lastTestedAt !== null
							? `Connexion réussie le ${formatLongDate(provider.lastTestedAt)}`
							: 'Connexion réussie'
					}
				/>
			);
		}

		return <Tag severity="danger" value="Connexion en échec" />;
	}, [provider]);

	return (
		<GridCard trackCount={PROVIDER_CARD_TRACK_COUNT} rowGap="0.875rem">
			<div css={headerStyle}>
				<h2 css={nameStyle}>{provider.label}</h2>
				<p css={subTitleStyle}>{provider.kind.label}</p>
			</div>
			<div css={bodyStyle}>
				<div css={fieldRowStyle}>
					<p css={fieldLabelStyle}>URL de base</p>
					<p css={fieldValueStyle}>{provider.baseUrl}</p>
				</div>
				<div css={badgeRowStyle}>
					<Tag
						severity="secondary"
						value={`Clé enregistrée le ${formatLongDate(provider.apiKeyRegisteredAt.toISOString())}`}
					/>
					{provider.isPlatformCredential ? (
						<Tag severity="info" value="Clé de la plateforme" />
					) : null}
					{testBadge}
				</div>
				{provider.lastTestSucceeded === false && provider.lastTestMessage !== null ? (
					<p css={testMessageStyle}>{provider.lastTestMessage}</p>
				) : null}
			</div>
			<ProviderCardActions provider={provider} />
		</GridCard>
	);
};

export default ProviderCard;
